import { useEffect } from "react"
import { create } from "zustand"

import { cn } from "@/lib/utils"
import { ObjectLoader } from "@/src/components/admin/siteAdmin/energy/Bills"
import { Document } from "@/src/graphql/public-types"
import { Portal } from "@radix-ui/react-portal"
import { IconPictureInPictureOn } from "@tabler/icons-react"

import { DraggablePIP } from "./DraggablePIP"
import { Button, Tooltip } from "./ui"

type Doc = Pick<Document, "pid" | "viewHref" | "contentType" | "fileName">
interface DocPreviewStore {
  docs: Map<string, Doc>
  openDoc: (doc?: Doc | null) => void
  closeDoc: (doc: Doc) => void
  active: string | null
  setActive: (doc: Doc | null) => void
  reset: () => void
}

export const useDocPreviewStore = create<DocPreviewStore>((set) => ({
  docs: new Map(),
  openDoc: (doc) =>
    set((prev) => {
      if (!doc) return prev
      const newBills = new Map(prev.docs)
      newBills.set(doc.pid, doc)
      return { docs: newBills, active: doc.pid }
    }),
  closeDoc: (doc) =>
    set((prev) => {
      const newBills = new Map(prev.docs)
      newBills.delete(doc.pid)
      return { docs: newBills }
    }),
  reset: () => set({ docs: new Map() }),
  active: null,
  setActive: (doc) =>
    set(() => {
      return { active: doc ? doc.pid : null }
    }),
}))

export const DocPreviewButton = ({
  doc,
  className,
}: {
  doc: Doc
  className?: string
}) => {
  const { openDoc } = useDocPreviewStore()
  const unsupportedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/octet-stream",
  ]
  const isUnsupported = unsupportedTypes.includes(doc.contentType)

  return (
    <Tooltip
      content={isUnsupported ? "Can't preview this file type" : "Open preview"}
      dark
      disableHoverableContent
    >
      <Button
        icon={IconPictureInPictureOn}
        iconOnly
        variant="neutral"
        noBorder
        disabled={isUnsupported}
        className={className}
        onClick={(e) => {
          e.stopPropagation()
          openDoc(doc)
        }}
      />
    </Tooltip>
  )
}

export const DocsPreview = () => {
  const { docs, closeDoc, reset, setActive, active } = useDocPreviewStore()
  const docsArr = Array.from(docs.values())

  // reset the store when this unmounts
  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  return (
    <Portal>
      <div className="pointer-events-auto">
        {docsArr.map((doc) => (
          <DraggablePIP
            key={doc.viewHref}
            onClose={() => closeDoc(doc)}
            defaultSize={{ width: 600, height: 700 }}
            minHeight={400}
            minWidth={400}
            onClick={() => setActive(doc)}
            className={cn({ "z-[999]": active === doc.pid })}
          >
            <span className="p-2 text-center text-h3">File: {doc.fileName}</span>
            <div className="grow overflow-auto">
              <ObjectLoader
                className="h-full"
                width="100%"
                height="100%"
                type={doc.contentType}
                data={doc.viewHref}
              />
            </div>
          </DraggablePIP>
        ))}
      </div>
    </Portal>
  )
}
